import {
  Avatar,
  Content,
  FavoriteToggleIcon,
  PageWithHeader,
  Progress,
  TableColumn,
} from '@backstage/core-components';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  EntityListProvider,
  EntityRefLink,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { getExplorerTableSx } from '../entity-explorer/shared';
import { shellMetrics } from '../../theme/tokens';
import { EntityRefChips, type EntityInfo } from '../common/EntityRefChips';
import { TeamsExplorerControls } from './explorer-page/TeamsExplorerControls';
import { useTeamsExplorerState } from './explorer-page/useTeamsExplorerState';
import type { TeamsEntityFilters } from './explorer-page/EntityMembershipFilter';

const countMembers = (entity: CatalogTableRow['entity']) =>
  (entity.relations ?? []).filter(relation => relation.type === 'hasMember')
    .length;

const getGroupDisplayName = (entity: CatalogTableRow['entity']) => {
  const profile = (
    entity.spec as { profile?: { displayName?: string } } | undefined
  )?.profile;
  return profile?.displayName ?? entity.metadata.title ?? entity.metadata.name;
};

const getGroupPicture = (entity: CatalogTableRow['entity']) =>
  (entity.spec as { profile?: { picture?: string } } | undefined)?.profile
    ?.picture;

const normaliseRef = (ref: string) =>
  stringifyEntityRef(
    parseEntityRef(ref, { defaultKind: 'User', defaultNamespace: 'default' }),
  );

const getLeadRefs = (entity: CatalogTableRow['entity']): string[] => {
  const roles = (entity.spec as Record<string, unknown> | undefined)?.roles as
    | Record<string, string[]>
    | undefined;
  return (roles?.['team-leads'] ?? []).map(normaliseRef);
};

const TeamsExplorerTable = () => {
  const theme = useTheme();
  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const { entities } = useEntityList<TeamsEntityFilters>();

  const allLeadRefs = useMemo(
    () => [...new Set(entities.flatMap(e => getLeadRefs(e)))],
    [entities],
  );

  const { value: entityMap = new Map<string, EntityInfo>() } =
    useAsync(async () => {
      if (!allLeadRefs.length) return new Map<string, EntityInfo>();

      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: allLeadRefs,
        fields: ['kind', 'metadata', 'spec'],
      });

      const map = new Map<string, EntityInfo>();
      for (let i = 0; i < allLeadRefs.length; i++) {
        const item = items[i];
        if (!item) continue;
        const profile = (item.spec as Record<string, unknown> | undefined)
          ?.profile as Record<string, string> | undefined;
        map.set(allLeadRefs[i], {
          displayName:
            profile?.displayName ?? item.metadata.title ?? item.metadata.name,
          picture: profile?.picture,
        });
      }
      return map;
    }, [allLeadRefs.join('|')]);

  const openAppRoute = (path: string) => {
    const appBaseUrl = configApi.getOptionalString('app.baseUrl');
    if (!appBaseUrl) {
      window.location.assign(path);
      return;
    }

    const targetUrl = new URL(path.replace(/^\/+/, ''), `${appBaseUrl}/`);
    window.location.assign(targetUrl.toString());
  };

  const columns: TableColumn<CatalogTableRow>[] = [
    {
      title: 'Name',
      field: 'resolved.entityRef',
      highlight: true,
      width: '24%',
      customSort: ({ entity: a }, { entity: b }) =>
        getGroupDisplayName(a).localeCompare(getGroupDisplayName(b)),
      customFilterAndSearch: (filter, { entity }) =>
        getGroupDisplayName(entity)
          .toLocaleLowerCase()
          .includes(filter.toLocaleLowerCase()),
      render: ({ entity }) => (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
          <Avatar
            picture={getGroupPicture(entity)}
            displayName={getGroupDisplayName(entity)}
            customStyles={{ width: 28, height: 28, fontSize: 11 }}
          />
          <EntityRefLink entityRef={entity} defaultKind="Group" />
        </Box>
      ),
    },
    {
      title: 'Description',
      field: 'entity.metadata.description',
      width: '38%',
      customFilterAndSearch: (filter, { entity }) =>
        entity.metadata.description
          ?.toLocaleLowerCase()
          .includes(filter.toLocaleLowerCase()) ?? false,
      render: ({ entity }) => entity.metadata.description ?? '—',
    },
    {
      title: 'Team Leads',
      field: 'entity.spec.roles.team-leads',
      width: '20%',
      customFilterAndSearch: (filter, { entity }) => {
        const refs = getLeadRefs(entity);
        return refs.some(ref =>
          (entityMap.get(ref)?.displayName ?? ref)
            .toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase()),
        );
      },
      render: ({ entity }) => (
        <EntityRefChips refs={getLeadRefs(entity)} entityMap={entityMap} />
      ),
    },
    {
      title: 'Members',
      field: 'entity.relations.length',
      width: '10%',
      customFilterAndSearch: (filter, { entity }) =>
        String(countMembers(entity)).includes(filter),
      render: ({ entity }) => countMembers(entity),
    },
  ];

  const actions = useMemo(
    () => [
      ({ entity }: CatalogTableRow) => {
        const namespace = entity.metadata.namespace ?? 'default';
        const kind = entity.kind.toLocaleLowerCase('en-US');
        const name = entity.metadata.name;

        return {
          icon: () => (
            <TableActionIcon
              icon={<OpenInNewOutlinedIcon fontSize="small" />}
              label="View"
            />
          ),
          onClick: () => openAppRoute(`/catalog/${namespace}/${kind}/${name}`),
          tooltip: 'Open team',
        };
      },
      ({ entity }: CatalogTableRow) => {
        const isStarred = isStarredEntity(entity);

        return {
          icon: () => (
            <TableActionIcon
              active={isStarred}
              icon={<FavoriteToggleIcon isFavorite={isStarred} />}
              label="Star"
            />
          ),
          onClick: () => toggleStarredEntity(entity),
          tooltip: isStarred ? 'Remove star' : 'Star team',
        };
      },
    ],
    [configApi, isStarredEntity, toggleStarredEntity],
  );

  return (
    <CatalogTable
      actions={actions}
      tableOptions={{
        actionsCellStyle: {
          color: theme.palette.text.secondary,
          textAlign: 'center',
        },
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        pageSize: 10,
        pageSizeOptions: [10, 20, 50],
        padding: 'dense',
        search: false,
        toolbar: false,
      }}
      columns={columns}
    />
  );
};

const TableActionIcon = ({
  active = false,
  icon,
  label,
}: {
  active?: boolean;
  icon: React.ReactNode;
  label: string;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        alignItems: 'center',
        color: active
          ? theme.palette.warning.main
          : theme.palette.text.secondary,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        lineHeight: 1,
        minWidth: 52,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
          height: 20,
          justifyContent: 'center',
          '& svg': {
            fontSize: 18,
          },
        }}
      >
        {icon}
      </Box>
      <Typography
        component="span"
        sx={{
          color: 'inherit',
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.01em',
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const TeamsExplorerContent = ({ userEntityRef }: { userEntityRef: string }) => {
  const theme = useTheme();
  const { searchText, setSearchText } = useTeamsExplorerState();

  return (
    <Stack spacing={2}>
      <TeamsExplorerControls
        searchText={searchText}
        setSearchText={setSearchText}
        userEntityRef={userEntityRef}
      />

      <Box
        sx={{
          ...getExplorerTableSx(theme, { linkColumnIndices: [] }),
          '& td:last-child > div': {
            gap: theme.spacing(0.25),
            justifyContent: 'center',
          },
          '& td:last-child button': {
            borderRadius: `${theme.shape.borderRadius * 1.5}px`,
            padding: theme.spacing(0.75, 0.5),
          },
          '& th:last-child': { textAlign: 'center' },
        }}
      >
        <TeamsExplorerTable />
      </Box>
    </Stack>
  );
};

export const CustomTeamsExplorerPage = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const orgName =
    configApi.getOptionalString('organization.name') ?? 'Meltwater';

  const { loading: loadingIdentity, value: userEntityRef } =
    useAsync(async () => {
      const identity = await identityApi.getBackstageIdentity();
      return identity.userEntityRef;
    }, [identityApi]);

  return (
    <PageWithHeader
      subtitle={`${orgName} team directory`}
      title="Teams"
      themeId="home"
    >
      <Content>
        <Box
          sx={{
            boxSizing: 'border-box',
            maxWidth: 'none',
            px: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          {loadingIdentity || !userEntityRef ? (
            <Progress />
          ) : (
            <Stack spacing={shellMetrics.sectionGap / 8}>
              <EntityListProvider<TeamsEntityFilters>
                pagination={{ limit: 10, mode: 'offset' }}
              >
                <TeamsExplorerContent userEntityRef={userEntityRef} />
              </EntityListProvider>
            </Stack>
          )}
        </Box>
      </Content>
    </PageWithHeader>
  );
};
